import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/widgets/toast';
import { HighlightService } from '../services/highlightService';
import {
  HighlightItem,
  UpdateHighlightOrderRequest,
} from '../types/highlight.types';

export const useHighlightManagement = (sectionKey: string) => {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<HighlightItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Query para buscar os itens da seção
  const {
    data: highlightItems,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['highlight-items', sectionKey],
    queryFn: () => HighlightService.getHighlightItems(sectionKey),
    enabled: !!sectionKey,
  });

  console.log('🔧 Hook - sectionKey:', sectionKey, 'highlightItems:', highlightItems?.length || 0, 'itens');

  // Effect para atualizar o estado local quando os dados mudam
  useEffect(() => {
    if (highlightItems) {
      setItems(highlightItems);
      setHasChanges(false);
    }
  }, [highlightItems]);

  // Mutation para atualizar a ordem
  const updateOrderMutation = useMutation({
    mutationFn: (itemsToUpdate: UpdateHighlightOrderRequest[]) =>
      HighlightService.updateHighlightOrder(sectionKey, itemsToUpdate),
    onSuccess: () => {
      toast.form.success(
        'Ordem atualizada com sucesso!',
        'As alterações na ordenação foram salvas.'
      );
      setHasChanges(false);
      // Invalida a query para refetch dos dados
      queryClient.invalidateQueries({
        queryKey: ['highlight-items', sectionKey],
      });
    },
    onError: (error: any) => {
      toast.api.error(
        'Erro ao atualizar ordem',
        error.message || 'Ocorreu um erro inesperado ao salvar as alterações.'
      );
    },
  });

  // Mutation para remover itens
  const removeItemsMutation = useMutation({
    mutationFn: (itemIds: string[]) =>{
      const itemsToRemove = itemIds.map(id => ({ id, order: null }));
      return HighlightService.removeItemsFromSection(sectionKey, itemsToRemove)
    },
    onSuccess: () => {
      toast.success(
        'Itens removidos com sucesso!',
        'Os itens foram removidos da seção.'
      );
      // Invalida as queries para refetch dos dados
      queryClient.invalidateQueries({
        queryKey: ['highlight-items', sectionKey],
      });
      queryClient.invalidateQueries({
        queryKey: ['available-items', sectionKey],
      });
    },
    onError: (error: any) => {
      toast.api.error(
        'Erro ao remover itens',
        error.message || 'Ocorreu um erro inesperado ao remover os itens.'
      );
    },
  });

  // Função para reordenar os itens
  const reorderItems = useCallback((startIndex: number, endIndex: number) => {
    const newItems = Array.from(items);
    const [removed] = newItems.splice(startIndex, 1);
    newItems.splice(endIndex, 0, removed);

    // Atualiza a ordem dos itens
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setItems(updatedItems);
    setHasChanges(true);
  }, [items]);

  // Função para salvar as alterações
  const saveChanges = useCallback(() => {
    if (!hasChanges) {
      toast.info('Nenhuma alteração para salvar', 'Não há alterações pendentes na ordenação.');
      return;
    }

    const itemsToUpdate: UpdateHighlightOrderRequest[] = items.map((item) => ({
      id: item.id,
      order: item.order,
    }));

    updateOrderMutation.mutate(itemsToUpdate);
  }, [hasChanges, items, updateOrderMutation]);

  // Função para resetar as alterações
  const resetChanges = useCallback(() => {
    if (highlightItems && Array.isArray(highlightItems)) {
      setItems(highlightItems);
      setHasChanges(false);
      toast.info('Alterações descartadas', 'A ordenação foi restaurada ao estado anterior.');
    }
  }, [highlightItems]);

  // Função para remover itens
  const removeItems = useCallback((itemIds: string[]) => {
    removeItemsMutation.mutate(itemIds);
  }, [removeItemsMutation]);

  return {
    items,
    isLoading,
    isError,
    hasChanges,
    isSaving: updateOrderMutation.isPending,
    isRemoving: removeItemsMutation.isPending,
    reorderItems,
    saveChanges,
    resetChanges,
    removeItems,
    refetch,
    // Função para forçar atualização após adicionar itens
    refreshItems: () => {
      queryClient.invalidateQueries({
        queryKey: ['highlight-items', sectionKey],
      });
    },
  };
};
